"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import type { FileCategoryNode } from "@/lib/files/server";

/** Slugs from root → active (inclusive). Empty when slug is missing. */
function findPathSlugs(nodes: FileCategoryNode[], slug: string): string[] {
  for (const node of nodes) {
    if (node.slug === slug) return [node.slug];
    const childPath = findPathSlugs(node.children ?? [], slug);
    if (childPath.length) return [node.slug, ...childPath];
  }
  return [];
}

function findNodeBySlug(
  nodes: FileCategoryNode[],
  slug: string,
): FileCategoryNode | null {
  for (const node of nodes) {
    if (node.slug === slug) return node;
    const found = findNodeBySlug(node.children ?? [], slug);
    if (found) return found;
  }
  return null;
}

const THEME_COLOR = "var(--association-theme-color-1)";

function Chevron({
  open,
  className = "",
}: {
  open: boolean;
  className?: string;
}) {
  return (
    <ChevronDown
      className={`shrink-0 transition-transform duration-200 ${
        open ? "" : "ltr:-rotate-90 rtl:rotate-90"
      } ${className}`}
      aria-hidden
    />
  );
}

function RootNode({
  node,
  locale,
  activeSlug,
  openSlugs,
  onToggle,
}: {
  node: FileCategoryNode;
  locale: string;
  activeSlug?: string;
  openSlugs: Set<string>;
  onToggle: (slug: string) => void;
}) {
  const children = node.children ?? [];
  const hasChildren = children.length > 0;
  const active = node.slug === activeSlug;
  const open = openSlugs.has(node.slug);
  const highlighted = active || open;

  return (
    <li className="border-b border-border/70 last:border-b-0">
      <div className="flex items-center gap-2 px-5 py-3.5">
        <Link
          href={`/${locale}/files/${node.slug}`}
          className="min-w-0 flex-1 truncate text-[15px] font-semibold transition-colors"
          style={highlighted ? { color: THEME_COLOR } : undefined}
        >
          {node.title}
        </Link>
        {hasChildren ? (
          <button
            type="button"
            aria-expanded={open}
            aria-label={open ? "Collapse" : "Expand"}
            onClick={() => onToggle(node.slug)}
            className="-me-1 flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground/70"
            style={open ? { color: THEME_COLOR } : undefined}
          >
            <Chevron open={open} className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {hasChildren && open ? (
        <ul className="space-y-2.5 pb-4 pe-5 ps-6">
          {children.map((child) => (
            <ChildNode
              key={child.id}
              node={child}
              locale={locale}
              activeSlug={activeSlug}
              openSlugs={openSlugs}
              onToggle={onToggle}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function ChildNode({
  node,
  locale,
  activeSlug,
  openSlugs,
  onToggle,
}: {
  node: FileCategoryNode;
  locale: string;
  activeSlug?: string;
  openSlugs: Set<string>;
  onToggle: (slug: string) => void;
}) {
  const children = node.children ?? [];
  const hasChildren = children.length > 0;
  const active = node.slug === activeSlug;
  const open = openSlugs.has(node.slug);

  return (
    <li>
      <div className="flex items-center gap-1.5">
        {hasChildren ? (
          <button
            type="button"
            aria-expanded={open}
            aria-label={open ? "Collapse" : "Expand"}
            onClick={() => onToggle(node.slug)}
            className="shrink-0 text-muted-foreground/60"
            style={active || open ? { color: THEME_COLOR } : undefined}
          >
            <Chevron open={open} className="h-3.5 w-3.5" />
          </button>
        ) : (
          <Chevron
            open={false}
            className="h-3.5 w-3.5 text-muted-foreground/60"
          />
        )}
        <Link
          href={`/${locale}/files/${node.slug}`}
          className="min-w-0 flex-1 truncate text-[13px] text-muted-foreground transition-colors hover:text-foreground"
          style={active ? { color: THEME_COLOR } : undefined}
        >
          {node.title}
        </Link>
      </div>

      {hasChildren && open ? (
        <ul className="mt-2.5 space-y-2.5 ps-4">
          {children.map((child) => (
            <ChildNode
              key={child.id}
              node={child}
              locale={locale}
              activeSlug={activeSlug}
              openSlugs={openSlugs}
              onToggle={onToggle}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function initialOpenSlugs(
  nodes: FileCategoryNode[],
  activeSlug: string | undefined,
  path: string[],
): Set<string> {
  const open = new Set<string>();
  // Open ancestors so the active node is visible.
  for (const slug of path.slice(0, -1)) open.add(slug);
  // If the active node itself has children, open it so its kids show.
  if (activeSlug) {
    const activeNode = findNodeBySlug(nodes, activeSlug);
    if (activeNode?.children?.length) open.add(activeSlug);
  }
  return open;
}

export function CategoryTree({
  nodes,
  locale,
  activeSlug,
  title,
}: {
  nodes: FileCategoryNode[];
  locale: string;
  activeSlug?: string;
  title?: string;
}) {
  const activePath = useMemo(
    () => (activeSlug ? findPathSlugs(nodes, activeSlug) : []),
    [nodes, activeSlug],
  );

  const [openSlugs, setOpenSlugs] = useState<Set<string>>(() =>
    initialOpenSlugs(nodes, activeSlug, activePath),
  );

  // When the user navigates to another category, ensure its ancestors stay open.
  useEffect(() => {
    setOpenSlugs((prev) => {
      const next = new Set(prev);
      for (const slug of activePath.slice(0, -1)) next.add(slug);
      if (activeSlug) {
        const activeNode = findNodeBySlug(nodes, activeSlug);
        if (activeNode?.children?.length) next.add(activeSlug);
      }
      return next;
    });
  }, [activePath, activeSlug, nodes]);

  const onToggle = useCallback((slug: string) => {
    setOpenSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }, []);

  if (!nodes.length) return null;

  return (
    <nav
      aria-label="File categories"
      className="overflow-hidden rounded-xl border border-border/80 bg-card shadow-sm"
    >
      {title ? (
        <div
          className="px-5 py-4 text-[15px] font-bold text-white"
          style={{ backgroundColor: THEME_COLOR }}
        >
          {title}
        </div>
      ) : null}
      <ul>
        {nodes.map((node) => (
          <RootNode
            key={node.id}
            node={node}
            locale={locale}
            activeSlug={activeSlug}
            openSlugs={openSlugs}
            onToggle={onToggle}
          />
        ))}
      </ul>
    </nav>
  );
}
